import { ChangeEvent, useEffect, useRef, useState } from 'react';

import { CheckCircleIcon, CloseIcon } from '@braskai/icons';
import { IconButton, TextField, Typography } from '@braskai/ui-kit';

import { CircularProgress, InputAdornment, Stack } from '@mui/material';

import { useInterfaceLanguage } from '@/shared/i18n';

const DEBOUNCE_DELAY_MS = 800;

type PromoCodeInputProps = {
  appliedCode?: string;
  error?: null | string;
  hint?: string;
  isApplying?: boolean;
  onApply: (code: string) => void;
  onRemove?: () => void;
};

export const PromoCodeInput = ({
  appliedCode,
  error,
  hint,
  isApplying = false,
  onApply,
  onRemove,
}: PromoCodeInputProps) => {
  const [value, setValue] = useState('');
  const { t } = useInterfaceLanguage();
  const debounceTimerRef = useRef<null | ReturnType<typeof setTimeout>>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounce timer
    if (newValue.trim()) {
      debounceTimerRef.current = setTimeout(() => {
        onApply(newValue.trim());
      }, DEBOUNCE_DELAY_MS);
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleRemove = () => {
    setValue('');
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    onRemove?.();
  };

  const isApplied = !!appliedCode;

  const renderEndAdornment = () => {
    if (isApplying) {
      return <CircularProgress size={20} />;
    }

    if (isApplied) {
      return (
        <Stack alignItems="center" direction="row" gap={4}>
          <CheckCircleIcon color="#0B0BCF" height={20} width={20} />
          <IconButton
            data-testid="promo-code-input-remove"
            size="small"
            sx={{ color: 'grey.500' }}
            disableFixSizeIcon
            onClick={handleRemove}
          >
            <CloseIcon height={16} width={16} />
          </IconButton>
        </Stack>
      );
    }

    return null;
  };

  return (
    <Stack gap={8}>
      <TextField
        slotProps={{
          input: {
            endAdornment: <InputAdornment position="end">{renderEndAdornment()}</InputAdornment>,
          },
        }}
        disabled={isApplied}
        error={!!error}
        placeholder={t('billing.confirm.promoPlaceholder')}
        size="small"
        value={isApplied ? appliedCode : value}
        variant="outlined"
        fullWidth
        onChange={handleChange}
      />
      {error && (
        <Typography color="error.500" variant="body-2">
          {error}
        </Typography>
      )}
      {hint && !error && (
        <Typography color="grey.500" variant="body-2">
          {hint}
        </Typography>
      )}
    </Stack>
  );
};
